import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AddBoxOutlined as AddBoxOutlinedIcon,
  Android as AndroidIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DownloadRounded as DownloadRoundedIcon,
  IosShare as IosShareIcon,
} from "@mui/icons-material";
import AppleIcon from "@mui/icons-material/Apple";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NaturalCaptureComposerShell from "../UI/NaturalCaptureComposerShell";
import colors from "../../assets/theme/colors";
import { PRODUCT_NAME_TITLE, PRODUCT_SITE_URL } from "../../constants/config";

const LANDING = {
  borderLight: colors.landing?.borderLight || "#E2E8F0",
  bodyText: colors.landing?.bodyText || "#334155",
  sageDeep: colors.landing?.sageDeep || "#6FA594",
  textMuted: colors.landing?.textMuted || "#64748B",
};

const PHONE_TYPES = {
  ios: "ios",
  android: "android",
};

const installSteps = {
  [PHONE_TYPES.ios]: [
    {
      title: "Tap the Share button",
      detail: "The square with an arrow in Safari's bottom bar.",
      preview: "safariShare",
    },
    {
      title: "Choose Add to Home Screen",
      detail: "Scroll down in the share sheet to find it.",
      preview: "addHome",
    },
    {
      title: "Tap Add",
      detail: `${PRODUCT_NAME_TITLE} drops onto your home screen.`,
      preview: "iosAdd",
    },
  ],
  [PHONE_TYPES.android]: [
    {
      title: "Tap the menu",
      detail: "Three dots in the top-right of Chrome.",
      preview: "chromeMenu",
    },
    {
      title: "Tap Install app",
      detail: 'Or "Add to Home screen" on some phones.',
      preview: "installApp",
    },
    {
      title: "Tap Install",
      detail: `${PRODUCT_NAME_TITLE} drops onto your home screen.`,
      preview: "androidInstall",
    },
  ],
};

const getHostName = () => {
  try {
    return new URL(PRODUCT_SITE_URL).host;
  } catch {
    return "lifelog.care";
  }
};

const StepNumber = ({ value }) => (
  <Box
    sx={{
      width: 34,
      height: 34,
      borderRadius: "50%",
      bgcolor: "#15211E",
      color: "#FFFFFF",
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
      fontSize: "0.94rem",
      fontWeight: 900,
    }}
  >
    {value}
  </Box>
);

const BrowserBar = ({ mode = "light" }) => {
  const dark = mode === "dark";
  return (
    <Box
      sx={{
        mt: 1.15,
        p: 0.7,
        borderRadius: 2,
        bgcolor: dark ? "#26302C" : "#F4F7F5",
        border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : alpha(LANDING.borderLight, 0.9)}`,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 0.7,
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          minHeight: 35,
          px: 1.25,
          borderRadius: 1.5,
          bgcolor: dark ? "#3B4742" : "#FFFFFF",
          color: dark ? "#DDE8E3" : LANDING.bodyText,
          display: "flex",
          alignItems: "center",
          gap: 0.45,
          fontSize: "0.8rem",
          fontWeight: 750,
        }}
      >
        <Box component="span" aria-hidden="true">🔒</Box>
        {getHostName()}
      </Box>
      {dark ? (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.22)",
            display: "grid",
            placeItems: "center",
            color: "#DDE8E3",
          }}
        >
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </Box>
      ) : (
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 1.6,
            border: `3px solid ${alpha("#6FA594", 0.35)}`,
            display: "grid",
            placeItems: "center",
            color: "#1677FF",
          }}
        >
          <IosShareIcon sx={{ fontSize: 22 }} />
        </Box>
      )}
    </Box>
  );
};

const MenuPreview = ({ type }) => {
  if (type === "addHome") {
    return (
      <Box sx={previewRowSx}>
        <AddBoxOutlinedIcon sx={{ fontSize: 18, color: "#15211E" }} />
        <Typography sx={previewTextSx}>Add to Home Screen</Typography>
        <AddBoxOutlinedIcon sx={{ ml: "auto", fontSize: 16, color: "#1677FF" }} />
      </Box>
    );
  }

  if (type === "installApp") {
    return (
      <Box sx={previewRowSx}>
        <DownloadRoundedIcon sx={{ fontSize: 19, color: "#1677FF" }} />
        <Typography sx={previewTextSx}>Install app</Typography>
      </Box>
    );
  }

  return (
    <Box sx={installPromptSx}>
      <img src="/lifelog-icon-192.png" alt="" width="38" height="38" />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, lineHeight: 1.05, color: "#15211E" }}>
          {type === "androidInstall" ? "Install app?" : PRODUCT_NAME_TITLE}
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: LANDING.textMuted, lineHeight: 1.2 }}>
          {getHostName()}
        </Typography>
      </Box>
      <Typography sx={{ ml: "auto", color: "#1677FF", fontWeight: 900 }}>
        {type === "androidInstall" ? "Install" : "Add"}
      </Typography>
    </Box>
  );
};

const previewRowSx = {
  mt: 1.15,
  minHeight: 56,
  px: 1.25,
  py: 1,
  borderRadius: 2,
  bgcolor: "#F4FAF7",
  border: `1px solid ${alpha("#6FA594", 0.24)}`,
  borderLeft: `3px solid ${LANDING.sageDeep}`,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const previewTextSx = {
  fontSize: "0.94rem",
  fontWeight: 900,
  color: "#15211E",
};

const installPromptSx = {
  mt: 1.15,
  minHeight: 70,
  px: 1.4,
  py: 1.2,
  borderRadius: 2,
  bgcolor: "#FFFFFF",
  border: `1px solid ${alpha(LANDING.borderLight, 0.95)}`,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const StepPreview = ({ type }) => {
  if (type === "safariShare") {
    return <BrowserBar />;
  }
  if (type === "chromeMenu") {
    return <BrowserBar mode="dark" />;
  }
  return <MenuPreview type={type} />;
};

const DonePreview = () => (
  <Box
    sx={{
      mt: 1,
      p: { xs: 2.1, sm: 2.4 },
      borderRadius: 3,
      bgcolor: "linear-gradient(180deg, #F0F6F2 0%, #F8F4EA 100%)",
      background: "linear-gradient(180deg, #F0F6F2 0%, #F8F4EA 100%)",
      textAlign: "center",
    }}
  >
    <Typography sx={{ color: LANDING.sageDeep, fontSize: "0.84rem", fontWeight: 950, letterSpacing: "0.06em" }}>
      AND YOU'RE DONE
    </Typography>
    <Box
      sx={{
        mt: 1.45,
        mx: "auto",
        width: 176,
        height: 116,
        borderRadius: 4,
        bgcolor: "#CDE1D8",
        border: "6px solid rgba(255,255,255,0.8)",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 1,
        p: 1.6,
        position: "relative",
      }}
    >
      <Typography sx={{ position: "absolute", top: 9, left: 13, fontSize: "0.68rem", fontWeight: 900, color: "#41534D" }}>
        9:41
      </Typography>
      {Array.from({ length: 8 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            mt: index < 4 ? 1.8 : 0,
            borderRadius: 1.6,
            bgcolor: "rgba(255,255,255,0.72)",
            display: "grid",
            placeItems: "center",
          }}
        >
          {index === 1 ? <img src="/lifelog-icon-192.png" alt="" width="26" height="26" /> : null}
        </Box>
      ))}
    </Box>
    <Typography sx={{ mt: 1.45, color: LANDING.bodyText, fontSize: "0.86rem", fontWeight: 800, lineHeight: 1.35 }}>
      Tap the icon anytime. It opens full-screen.
    </Typography>
  </Box>
);

const LandingInstallSheet = ({ open, onClose }) => {
  const [phoneType, setPhoneType] = useState(PHONE_TYPES.ios);
  const activeSteps = installSteps[phoneType];
  const browserName = phoneType === PHONE_TYPES.ios ? "Safari" : "Chrome";

  return (
    <NaturalCaptureComposerShell
      open={open}
      onClose={onClose}
      title={`Add ${PRODUCT_NAME_TITLE} to your home screen`}
      subtitle="Takes about 10 seconds"
      titleLeadingContent={(
        <Box
          sx={{
            width: 58,
            height: 58,
            borderRadius: 3,
            bgcolor: "#E6F4ED",
            display: "grid",
            placeItems: "center",
            boxShadow: `0 8px 22px ${alpha(LANDING.sageDeep, 0.22)}`,
            flexShrink: 0,
          }}
        >
          <img src="/lifelog-icon-192.png" alt="" width="45" height="45" />
        </Box>
      )}
      sheetSx={{
        maxWidth: { sm: 620 },
        mx: "auto",
        bgcolor: "#FFFFFF",
      }}
      bodySx={{
        gap: 2,
        px: { xs: 2.1, sm: 3.5 },
      }}
      footer={(
        <Stack spacing={1.1} alignItems="center">
          <Button
            type="button"
            variant="text"
            onClick={onClose}
            sx={{
              color: LANDING.bodyText,
              fontWeight: 900,
              textTransform: "none",
              textDecoration: "underline",
              textUnderlineOffset: 4,
            }}
          >
            just start in your browser →
          </Button>
          <Typography sx={{ color: LANDING.textMuted, fontSize: "0.78rem", fontWeight: 800, textAlign: "center" }}>
            Free · No account to install · Always up to date
          </Typography>
        </Stack>
      )}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: "#ECF6F2",
            border: `1px solid ${alpha(LANDING.sageDeep, 0.18)}`,
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="flex-start">
            <CheckCircleOutlineIcon sx={{ color: LANDING.sageDeep, fontSize: 22, mt: 0.15 }} />
            <Typography sx={{ color: LANDING.bodyText, fontSize: "0.96rem", lineHeight: 1.45 }}>
              <Box component="span" sx={{ fontWeight: 900, color: "#15211E" }}>
                The care app that skips the app store.
              </Box>{" "}
              No download and no updates. You are saving a shortcut that opens like an app.
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Typography sx={{ mb: 0.8, color: LANDING.sageDeep, textAlign: "center", fontSize: "0.82rem", fontWeight: 900 }}>
            • Pick your phone below
          </Typography>
          <Box
            role="tablist"
            aria-label="Choose phone type"
            sx={{
              p: 0.35,
              borderRadius: 2,
              border: `1px solid ${alpha(LANDING.borderLight, 0.9)}`,
              bgcolor: "#F7FAF8",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.35,
            }}
          >
            {[
              { value: PHONE_TYPES.ios, label: "iPhone & iPad", icon: <AppleIcon sx={{ fontSize: 17 }} /> },
              { value: PHONE_TYPES.android, label: "Android", icon: <AndroidIcon sx={{ fontSize: 17 }} /> },
            ].map((option) => {
              const selected = option.value === phoneType;
              return (
                <ButtonBase
                  key={option.value}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setPhoneType(option.value)}
                  sx={{
                    minHeight: 46,
                    borderRadius: 1.6,
                    px: 1,
                    color: selected ? "#15211E" : LANDING.textMuted,
                    bgcolor: selected ? "#FFFFFF" : "transparent",
                    boxShadow: selected ? "0 2px 9px rgba(15, 23, 42, 0.12)" : "none",
                    fontWeight: 900,
                    gap: 0.7,
                  }}
                >
                  {option.icon}
                  <Box component="span" sx={{ fontSize: { xs: "0.86rem", sm: "0.94rem" }, lineHeight: 1.1 }}>
                    {option.label}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        <Typography sx={{ color: LANDING.bodyText, fontSize: "0.9rem", fontWeight: 850 }}>
          Open {getHostName()} in{" "}
          <Box component="span" sx={{ px: 0.9, py: 0.25, borderRadius: 999, bgcolor: "#E9F4EF", color: LANDING.sageDeep, fontWeight: 950 }}>
            {browserName}
          </Box>
        </Typography>

        <Stack spacing={2.15}>
          {activeSteps.map((step, index) => (
            <Box
              key={step.title}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto minmax(0, 1fr)",
                gap: 1.25,
                pb: index === activeSteps.length - 1 ? 0 : 2.1,
                borderBottom: index === activeSteps.length - 1 ? "none" : `1px dashed ${alpha(LANDING.borderLight, 0.95)}`,
              }}
            >
              <StepNumber value={index + 1} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "#15211E", fontSize: "1rem", fontWeight: 900, lineHeight: 1.25 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ mt: 0.35, color: LANDING.textMuted, fontSize: "0.88rem", lineHeight: 1.35 }}>
                  {step.detail}
                </Typography>
                <StepPreview type={step.preview} />
              </Box>
            </Box>
          ))}
        </Stack>

        <DonePreview />
      </Stack>
    </NaturalCaptureComposerShell>
  );
};

export default LandingInstallSheet;
